import CircleButton from '@/circle/components/CircleButton'
import ActionsMenu from '@/common/atoms/actionsMenu/ActionsMenu'
import CopyLinkMenuItem from '@/common/atoms/actionsMenu/CopyLinkMenuItem'
import DeleteMenuItem from '@/common/atoms/actionsMenu/DeleteMenuItem'
import EditMenuItem from '@/common/atoms/actionsMenu/EditMenuItem'
import UnarchiveMenuItem from '@/common/atoms/actionsMenu/UnarchiveMenuItem'
import TitleLink from '@/common/atoms/TitleLink'
import Loading from '@/common/atoms/Loading'
import { NextStepFormsContext } from '@/nextStep/contexts/NextStepFormsContext'
import useNextStepForms from '@/nextStep/hooks/useNextStepForms'
import ScrollableLayout from '@/common/atoms/ScrollableLayout'
import { Title } from '@/common/atoms/Title'
import Page404 from '@/common/pages/Page404'
import { useOrgContext } from '@/org/contexts/OrgContext'
import ParticipantsNumber from '@/participants/components/ParticipantsNumber'
import { TaskFormsContext } from '@/task/contexts/TaskFormsContext'
import useTaskForms from '@/task/hooks/useTaskForms'
import VisibilityIcon from '@/visibility/components/VisibilityIcon'
import {
  Box,
  BoxProps,
  Flex,
  Heading,
  HStack,
  IconButton,
  Spacer,
  Tag,
  Tooltip,
  useDisclosure,
  Wrap,
} from '@chakra-ui/react'
import { useUpdateThreadMutation } from '@gql'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { PinIcon, PinnedIcon } from 'src/icons'
import settings from 'src/settings'
import { ThreadContext } from '../contexts/ThreadContext'
import useThreadState from '../hooks/useThreadState'
import useThreadStatus from '../hooks/useThreadStatus'
import ThreadEditModal from '../modals/ThreadEditModal'
import ThreadActivities from './ThreadActivities'
import ThreadActivityCreate from './ThreadActivityCreate'
import ThreadStatusIcon from './ThreadStatusIcon'
import { ThreadStatusMenu } from './ThreadStatusMenu'
import ThreadTypeBadge from './ThreadTypeBadge'

interface Props extends BoxProps {
  id: string
  changeTitle?: boolean
  headerIcons?: React.ReactNode
  // Closes whatever holds this thread, when there is something to close.
  // Passed by ThreadModal, absent on the thread page (REU02-B).
  onResolved?(): void
}

export default function ThreadContent({
  id,
  changeTitle,
  headerIcons,
  onResolved,
  ...boxProps
}: Props) {
  const { t } = useTranslation()
  const { orgData } = useOrgContext()
  const [updateThread] = useUpdateThreadMutation()
  // Shared by the activity stream (which renders the forms) and the composer
  // bar (whose button adds one). Tied to `id` so that reusing this component
  // for another tension never carries its open forms over.
  const nextStepForms = useNextStepForms(id)
  const taskForms = useTaskForms(id)

  // Load thread and activities
  const threadState = useThreadState(id)

  const {
    thread,
    path,
    loading,
    error,
    circle,
    participants,
    canEdit,
    canParticipate,
  } = threadState

  // Create modal
  const editModal = useDisclosure()

  const { threadStatus, setStatus } = useThreadStatus(thread)

  // Archive / unarchive
  const handleArchive = useCallback(
    () => updateThread({ variables: { id, values: { archivedAt: new Date().toISOString() } } }),
    [id]
  )
  const handleUnarchive = useCallback(
    () => updateThread({ variables: { id, values: { archivedAt: null } } }),
    [id]
  )

  // Pin / unpin for everyone
  const handleTogglePin = useCallback(
    () =>
      updateThread({
        variables: { id, values: { pinned: !thread?.pinned } },
      }),
    [id, thread?.pinned]
  )

  if (error) {
    console.error(error)
    return <Page404 />
  }

  const title = thread?.title || '…'

  return (
    <ThreadContext.Provider value={threadState}>
      <NextStepFormsContext.Provider value={nextStepForms}>
      <TaskFormsContext.Provider value={taskForms}>
      {changeTitle && <Title>{title}</Title>}

      <ScrollableLayout
        {...boxProps}
        header={
          <>
            <Wrap spacing={4} flex={1} ml={3} align="center">
              <HStack spacing={2} align="center">
                <ThreadStatusIcon
                  value={threadStatus}
                  readOnly={!canEdit}
                  onChange={setStatus}
                />

                <Heading as="h1" size="md">
                  {canEdit ? (
                    <TitleLink href="#" onClick={editModal.onOpen}>
                      {title}
                    </TitleLink>
                  ) : (
                    title
                  )}
                </Heading>

                {thread && <ThreadTypeBadge type={thread.type} />}
              </HStack>

              <Spacer />

              <HStack spacing={2}>
                {canEdit ? (
                  <Tooltip
                    label={t(
                      thread?.pinned
                        ? 'ThreadContent.unpin'
                        : 'ThreadContent.pin'
                    )}
                    hasArrow
                  >
                    <IconButton
                      aria-label={t(
                        thread?.pinned
                          ? 'ThreadContent.unpin'
                          : 'ThreadContent.pin'
                      )}
                      size="sm"
                      variant="ghost"
                      color={thread?.pinned ? 'yellow.500' : undefined}
                      icon={
                        thread?.pinned ? (
                          <PinnedIcon size={20} />
                        ) : (
                          <PinIcon size={20} />
                        )
                      }
                      onClick={handleTogglePin}
                    />
                  </Tooltip>
                ) : (
                  thread?.pinned && (
                    <Tooltip label={t('ThreadContent.pinned')} hasArrow>
                      <Box color="yellow.500">
                        <PinnedIcon size={20} />
                      </Box>
                    </Tooltip>
                  )
                )}

                {thread && (
                  <VisibilityIcon
                    objectType="thread"
                    circleId={thread.circleId}
                    level={thread.visibility}
                    size={20}
                  />
                )}

                <Box>
                  {canEdit && threadStatus && !thread?.archivedAt && (
                    <ThreadStatusMenu
                      value={threadStatus}
                      onChange={setStatus}
                    />
                  )}
                </Box>

                {thread?.archivedAt && <Tag>{t('common.archived')}</Tag>}

                {circle && <CircleButton circle={circle} />}

                <Box>
                  <ParticipantsNumber participants={participants} />
                </Box>
              </HStack>
            </Wrap>

            <Flex mr={headerIcons ? -2 : 0}>
              {canEdit && (
                <ActionsMenu ml={2}>
                  <EditMenuItem onClick={editModal.onOpen} />
                  <CopyLinkMenuItem url={`${settings.url}${path}`} />
                  {!thread?.archivedAt && (
                    <DeleteMenuItem onClick={handleArchive} />
                  )}
                  {thread?.archivedAt && (
                    <UnarchiveMenuItem onClick={handleUnarchive} />
                  )}
                </ActionsMenu>
              )}
              {headerIcons}
            </Flex>
          </>
        }
        footer={
          thread && canParticipate ? (
            <ThreadActivityCreate
              thread={thread}
              w="100%"
              p={5}
              onResolved={onResolved}
            />
          ) : undefined
        }
      >
        {loading && <Loading active center />}
        <ThreadActivities />
      </ScrollableLayout>

      {editModal.isOpen && (
        <ThreadEditModal isOpen thread={thread} onClose={editModal.onClose} />
      )}
      </TaskFormsContext.Provider>
      </NextStepFormsContext.Provider>
    </ThreadContext.Provider>
  )
}
